import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Container, Paper, Dialog, DialogContent, DialogActions, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import WritingTestUI from '../components/IELTS/WritingTestUI';
import TestModeModal from '../components/IELTS/TestModeModal';
import { hasAIScore, shouldShowScore } from '../components/IELTS/utils/scoreHelpers';
import { loadIeltsDraftStateFromBrowser } from '../components/IELTS/utils/ieltsDraftStorage';
import { useStoredUserData } from '../authentication/userSession';

const WritingTestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [testStarted, setTestStarted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [modeModalOpen, setModeModalOpen] = useState(true);
  const [selectedMode, setSelectedMode] = useState(null);
  const [selectedSectionIndices, setSelectedSectionIndices] = useState([0,1]);
  const [selectedTimeLimit, setSelectedTimeLimit] = useState(60);

  // Get the test duration from location state or use default
  const testDuration = selectedTimeLimit;
  const testData = location.state?.testData;
  const resumeDraft = Boolean(location.state?.resumeDraft);
  const userData = useStoredUserData() || {};
  const requiresSubscriptionForAiScoring = testData?.access
    ? !testData.access.canUseAiScoring
    : !userData?.IsSubscribed;

  useEffect(() => {
    if (!resumeDraft || !testData) return;
    const draft = loadIeltsDraftStateFromBrowser({ scope: 'sectional', section: 'writing', testId: testData.id });
    const savedMode = draft?.currentStep?.mode;
    if (!savedMode) return;
    setSelectedMode(savedMode);
    if (Array.isArray(draft?.currentStep?.selectedSectionIndices)) {
      setSelectedSectionIndices(draft.currentStep.selectedSectionIndices);
    }
    if (draft?.timeState?.timer?.minutes && draft.timeState.timer.minutes !== 'Unlimited') {
      setSelectedTimeLimit(Number(draft.timeState.timer.minutes));
    }
    setModeModalOpen(false);
    setTestStarted(true);
  }, [resumeDraft, testData]);

  const handleStartPracticeMode = (selectedParts = [1,2], timeLimit = 60) => {
    setSelectedMode('practice');
    setSelectedSectionIndices(selectedParts.map(p => p - 1));
    setSelectedTimeLimit(timeLimit);
    setModeModalOpen(false);
    setTestStarted(true);
  };
  const handleStartSimulationMode = () => {
    setSelectedMode('simulation');
    setSelectedSectionIndices([0,1]);
    setSelectedTimeLimit(60);
    setModeModalOpen(false);
    setTestStarted(true);
  };

  const handleTestComplete = (results) => {
    setTestResults(results);
    setSubmitted(true);
  };

  const handleCloseResults = () => {
    setShowResults(false);
    navigate('/ielts-dashboard');
  };

  if (!testStarted) {
    return (
      <Box>
        <TestModeModal
          open={modeModalOpen}
          onClose={() => {
            setModeModalOpen(false);
            navigate('/ielts-dashboard');
          }}
          onStartPracticeMode={handleStartPracticeMode}
          onStartSimulationMode={handleStartSimulationMode}
          initialPartNumbers={location.state?.plannerTaskId ? location.state.plannerPartNumbers : undefined}
          initialMinutes={location.state?.plannerTaskId ? location.state.plannerMinutes : undefined}
          numParts={2}
          defaultTime={60}
          timeOptions={[60, 55, 50, 45]}
          partLabels={["Task 1", "Task 2"]}
          fullPartsLabel="Full test (2 tasks - 60 minutes)"
        />
      </Box>
    );
  }

  if (!testData) {
    return <Typography variant="h6" color="error">No test data found. Please return to the dashboard and try again.</Typography>;
  }

  if (submitted) {
    const status = testResults?.response?.status;
    const hasAI = testResults ? hasAIScore(testResults) : false;
    const scoreAvailable = testResults ? shouldShowScore(testResults) : false;
    
    // PRIORITY: If AI scoring exists OR score is available, ALWAYS show results - NO DIALOG
    // This handles cases where AI returns results even with low confidence or band 0
    if (hasAI || scoreAvailable) {
      return (
        <WritingTestUI
          timeLimit={testDuration}
          mode={selectedMode || 'practice'}
          onComplete={handleTestComplete}
          sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
          selectedSectionIndices={selectedSectionIndices}
          testId={testData.id}
          apiResult={testResults}
          testType={testData.test_type}
          requiresSubscriptionForAiScoring={requiresSubscriptionForAiScoring}
          restoreDraft={resumeDraft}
        />
      );
    }
    
    // Only show dialog if NO results are available (pending evaluation with no AI scoring)
    const isPending = status === 'pending_evaluation';
    if (isPending) {
      return (
        <>
          <WritingTestUI
            timeLimit={testDuration}
            mode={selectedMode || 'practice'}
            onComplete={handleTestComplete}
            sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
            selectedSectionIndices={selectedSectionIndices}
            testId={testData.id}
            apiResult={testResults}
            testType={testData.test_type}
            requiresSubscriptionForAiScoring={requiresSubscriptionForAiScoring}
            restoreDraft={resumeDraft}
          />
          <Dialog open={true} disableEscapeKeyDown={true}>
            <DialogContent>
              <Typography variant="h4" sx={{ color: '#37965E', fontWeight: 700, mb: 3, textAlign: 'center' }}>
                Your test has been submitted.
              </Typography>
              <Typography variant="body1" sx={{ color: '#333', textAlign: 'center', mb: 4 }}>
                Results are pending evaluation. Please check back shortly or visit the results page.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button variant="outlined" color="primary" onClick={() => navigate('/ielts-results')}>
                  View Results Page
                </Button>
                <Button variant="contained" color="primary" onClick={() => navigate('/ielts-dashboard')}>
                  Return to Dashboard
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        </>
      );
    }
    
    // Fallback: show results if status is completed (even without AI scoring)
    const isCompleted = status === 'completed';
    if (isCompleted) {
      return (
        <WritingTestUI
          timeLimit={testDuration}
          mode={selectedMode || 'practice'}
          onComplete={handleTestComplete}
          sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
          selectedSectionIndices={selectedSectionIndices}
          testId={testData.id}
          apiResult={testResults}
          testType={testData.test_type}
          requiresSubscriptionForAiScoring={requiresSubscriptionForAiScoring}
          restoreDraft={resumeDraft}
        />
      );
    }
  }

  return (
    <>
      <WritingTestUI
        timeLimit={testDuration}
        mode={selectedMode || 'practice'}
        onComplete={handleTestComplete}
        sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
        selectedSectionIndices={selectedSectionIndices}
      testId={testData.id}
      testType={testData.test_type}
      requiresSubscriptionForAiScoring={requiresSubscriptionForAiScoring}
      restoreDraft={resumeDraft}
    />
    </>
  );
};

export default WritingTestPage; 
